import { useEffect, useState } from 'react';
import { Observable, combineLatest, distinctUntilChanged, map, shareReplay } from 'rxjs';
import { offline } from '@/infrastructure/api/v1/client';
import { connectionUx$ } from './connectionUx';

// What the editor can't do while it is offline: anything that needs Lureo's servers rather than the scene's own saves,
// which are answered on the device and sent in the background (offlineSaves.ts). Its controls are disabled with the
// reason as their tooltip.

export const OFFLINE_REASON = 'Not available offline';
export const SENDING_REASON = 'Available once your offline changes are saved';

/**
 * Why a control that needs Lureo's servers is disabled, or null when it isn't: offline once that outlasts a blip (as the
 * offline toast shows), or with changes that aren't going through yet (made offline and still sending, or failing to
 * send). A save on its way as usual disables nothing: what needs the servers is sent after it.
 */
export const unavailableReason$: Observable<string | null> = combineLatest([connectionUx$, offline.stalled$]).pipe(
  map(([ux, stalled]) => (ux.phase === 'offline' && ux.visible ? OFFLINE_REASON : stalled ? SENDING_REASON : null)),
  distinctUntilChanged(),
  shareReplay({ bufferSize: 1, refCount: true }),
);

/** The reason now, for a handler that runs outside render (a keyboard shortcut). */
export function unavailableReason(): string | null {
  let reason: string | null = null;
  unavailableReason$.subscribe(value => (reason = value)).unsubscribe();
  return reason;
}

/** The reason, kept current: disable a control that needs Lureo's servers while it isn't null. */
export function useUnavailableReason(): string | null {
  const [reason, setReason] = useState(unavailableReason);
  useEffect(() => {
    const subscription = unavailableReason$.subscribe(setReason);
    return () => subscription.unsubscribe();
  }, []);
  return reason;
}
